import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, LogOut, Calendar, Award, Gift, TrendingUp, 
  Copy, Check, Star, Mail, Phone, Clock, DollarSign
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    // Check if customer is logged in
    const storedCustomer = sessionStorage.getItem('customer');
    if (!storedCustomer) {
      navigate('/customer-login');
      return;
    }

    setCustomer(JSON.parse(storedCustomer));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [profileRes, bookingsRes, loyaltyRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-portal/me`, {
          credentials: 'include',
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-portal/bookings`, {
          credentials: 'include',
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-portal/loyalty-benefits`, {
          credentials: 'include',
        }),
      ]);

      if (!profileRes.ok || !bookingsRes.ok || !loyaltyRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const profileData = await profileRes.json();
      const bookingsData = await bookingsRes.json();
      const loyaltyData = await loyaltyRes.json();

      setCustomer(profileData);
      setBookings(bookingsData);
      setLoyaltyInfo(loyaltyData);
    } catch (error) {
      toast.error('Failed to load portal data');
      navigate('/customer-login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/customer/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      sessionStorage.removeItem('customer');
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const copyReferralCode = () => {
    if (loyaltyInfo?.referral_code) {
      navigator.clipboard.writeText(loyaltyInfo.referral_code);
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      Bronze: 'bg-orange-900/20 text-orange-500 border-orange-500/50',
      Silver: 'bg-gray-400/20 text-gray-300 border-gray-400/50',
      Gold: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      Platinum: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    };
    return colors[tier] || colors.Bronze;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-900/20 text-yellow-500',
      confirmed: 'bg-blue-900/20 text-blue-400',
      'in-progress': 'bg-purple-900/20 text-purple-400',
      completed: 'bg-green-900/20 text-green-400',
      cancelled: 'bg-red-900/20 text-red-400',
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 py-8 px-4 pt-24">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, {customer?.name}!
            </h1>
            <p className="text-gray-400">{customer?.email}</p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Home
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Loyalty Status Card */}
        {loyaltyInfo && (
          <Card className="bg-gradient-to-r from-red-900/20 to-red-800/10 border-red-500/30 mb-8">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-white flex items-center space-x-2">
                    <Award className="w-6 h-6" />
                    <span>Loyalty Status</span>
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    Earn rewards with every booking
                  </CardDescription>
                </div>
                <Badge className={`text-lg px-4 py-2 ${getTierColor(loyaltyInfo.current_tier)}`}>
                  {loyaltyInfo.current_tier}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress to Next Tier */}
              {loyaltyInfo.tier_info.next_tier && (
                <div>
                  <div className="flex justify-between text-sm text-gray-300 mb-2">
                    <span>Progress to {loyaltyInfo.tier_info.next_tier}</span>
                    <span>{loyaltyInfo.tier_info.bookings_needed} bookings to go</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-red-600 to-red-500 h-3 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, ((loyaltyInfo.total_bookings % 3) / 3) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Current Benefits */}
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center space-x-2">
                  <Gift className="w-4 h-4" />
                  <span>Your {loyaltyInfo.current_tier} Benefits</span>
                </h4>
                <ul className="space-y-1">
                  {loyaltyInfo.tier_info.benefits.map((benefit, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-center space-x-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Referral Code */}
              {loyaltyInfo.referral_code && (
                <div className="pt-4 border-t border-zinc-800">
                  <h4 className="text-white font-semibold mb-2">Your Referral Code</h4>
                  <div className="flex items-center space-x-2">
                    <code className="bg-zinc-800 text-red-400 px-4 py-2 rounded-lg flex-1 font-mono text-lg">
                      {loyaltyInfo.referral_code}
                    </code>
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="border-zinc-700 text-white hover:bg-zinc-800"
                    >
                      {copiedCode ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    Share this code with friends! They get $30 off, and you earn $30 credit.
                    {loyaltyInfo.total_referrals > 0 && (
                      <span className="text-green-400 ml-1">
                        ({loyaltyInfo.total_referrals} referrals made!)
                      </span>
                    )}
                  </p>
                  {loyaltyInfo.pending_referral_discount > 0 && (
                    <div className="mt-2 bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                      <p className="text-green-400 font-semibold">
                        💰 You have ${loyaltyInfo.pending_referral_discount} in referral credits!
                      </p>
                      <p className="text-sm text-gray-300">
                        Book your next service to use it automatically
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Bookings</p>
                  <p className="text-3xl font-bold text-white">{customer?.total_bookings || 0}</p>
                </div>
                <Calendar className="w-10 h-10 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Spent</p>
                  <p className="text-3xl font-bold text-white">
                    ${customer?.total_spent?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Loyalty Discount</p>
                  <p className="text-3xl font-bold text-white">
                    {loyaltyInfo?.tier_info.discount || '0%'}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Booking History */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Booking History</CardTitle>
            <CardDescription className="text-gray-400">
              View all your past and upcoming bookings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No bookings yet</p>
                <Button
                  onClick={() => navigate('/booking')}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Book Your First Detail
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-red-500/50 transition-all"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-white font-semibold text-lg">
                          {booking.service} - {booking.vehicleType}
                        </h4>
                        <p className="text-gray-400 text-sm">
                          {new Date(booking.preferredDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}{' '}
                          at {booking.preferredTime}
                        </p>
                      </div>
                      <Badge className={getStatusColor(booking.status)}>
                        {booking.status}
                      </Badge>
                    </div>

                    {booking.addons && booking.addons.length > 0 && (
                      <div className="mb-3">
                        <p className="text-gray-400 text-sm">
                          Add-ons: {booking.addons.join(', ')}
                        </p>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-zinc-700">
                      <span className="text-white font-bold text-xl">
                        ${booking.total_price.toFixed(2)}
                      </span>
                      <Button
                        onClick={() => navigate('/booking')}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Book Again
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Book New Service Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/booking')}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white font-semibold"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book a New Service
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;
